<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { toast } from '~/utils/sonner'

type MetricSortDirection = 'asc' | 'desc'
type MetricSortKey =
  | 'version'
  | 'platform'
  | 'installCount'
  | 'users'
  | 'heartbeatCount'
  | 'launchCount'
  | 'usageEventCount'
  | 'trafficBytes'
  | 'lastSeenAt'
type AppleMetricPlatform = AppleVersionMetrics['platform'] | 'all'
type AppleMetricRow = Omit<AppleVersionMetrics, 'platform'> & {
  platform: AppleMetricPlatform
  sourcePlatformCount: number
}

const dayjs = useDayjs()
const metricWindowDays = ref(1)
const metricWindowOptions = [1, 7, 30, 90]
const mergePlatforms = ref(true)
const metricSort = ref<{ key: MetricSortKey, direction: MetricSortDirection }>({
  key: 'version',
  direction: 'desc',
})
const metricsLoading = ref(false)
const metricsError = ref('')
const versionMetrics = ref<AppleVersionMetrics[]>([])
const testFlightLoading = ref(false)
const testFlightError = ref('')
const testFlightFetchedAt = ref('')
const testFlightApps = ref<AppleTestFlightAppStatus[]>([])

const metricRows = computed(() => sortMetricRows(metricDisplayRows.value))

// Pricing-disabled App Store builds show website purchase links only in these storefronts.
const storefronts = ref<string[]>([])
const savedStorefronts = ref<string[]>([])
const storefrontInput = ref('')
const storefrontsLoaded = ref(false)
const storefrontsLoading = ref(false)
const storefrontsSaving = ref(false)
const storefrontsError = ref('')
const storefrontsDirty = computed(() => storefronts.value.join(',') !== savedStorefronts.value.join(','))

function addStorefronts() {
  const codes = storefrontInput.value.split(/[\s,，]+/).map(code => code.trim().toUpperCase()).filter(Boolean)
  storefronts.value = [...new Set([...storefronts.value, ...codes])].sort()
  storefrontInput.value = ''
}

function removeStorefront(code: string) {
  storefronts.value = storefronts.value.filter(item => item !== code)
}

function acceptStorefronts(list: string[]) {
  storefronts.value = [...list]
  savedStorefronts.value = [...list]
  storefrontsLoaded.value = true
}

async function refreshStorefronts() {
  storefrontsLoading.value = true
  storefrontsError.value = ''
  try {
    acceptStorefronts((await useAdmin.getExternalPurchaseLinkStorefronts()).storefronts)
  } catch (error) {
    storefrontsError.value = errorText(error)
  } finally {
    storefrontsLoading.value = false
  }
}

async function saveStorefronts() {
  storefrontsSaving.value = true
  try {
    acceptStorefronts((await useAdmin.saveExternalPurchaseLinkStorefronts(storefronts.value)).storefronts)
    toast({ title: '已保存网站购买链接地区' })
  } catch (error) {
    toast({ title: '保存失败', description: errorText(error), variant: 'destructive' })
  } finally {
    storefrontsSaving.value = false
  }
}
// iOS builds ship from the TestFlight deploy script, so notes are added here per build afterwards.
type IosReleaseNotes = {
  build: number
  version: string
  channel: 'stable' | 'beta'
  uploadedAt: string | null
  inBeta: boolean
  inStable: boolean
  changelog: string
  changelog_en: string
  revision: string | null
}
const releaseNotes = ref<IosReleaseNotes[]>([])
// Newest first from PandaUnion; older builds are rarely changed.
const RECENT_BUILDS = 10
const recentReleaseNotes = computed(() => releaseNotes.value.slice(0, RECENT_BUILDS))
const releaseNotesLoading = ref(false)
const releaseNotesError = ref('')
const editingNotes = ref<IosReleaseNotes | null>(null)
const notesSheetOpen = computed({
  get: () => Boolean(editingNotes.value),
  set: (open: boolean) => { if (!open) editingNotes.value = null },
})
const iosBuildLabel = (item: { version: string, build: number }) => `${item.version} (${item.build})`
const groupsLabel = (item: IosReleaseNotes) => [item.inStable && 'Stable', item.inBeta && 'Beta'].filter(Boolean).join(' · ')
const stableBusy = ref<number | null>(null)

// Same as adding the build to the "iOS Stable" group in TestFlight by hand: beta keeps it.
async function setStable(item: IosReleaseNotes, stable: boolean) {
  if (stableBusy.value !== null) return
  // Lock the buttons while the dialog is open, so a second row cannot queue another change.
  stableBusy.value = item.build
  const label = iosBuildLabel(item)
  const confirmed = await useDialog().confirm(stable
    ? {
        title: `把 ${label} 设为正式版？`,
        body: '加入 TestFlight「iOS Stable」组，正式版用户会收到这个 build。测试版组里的保留不变。',
        confirmButtonText: '设为正式版',
        cancelButtonText: '取消',
        confirmButtonVariant: 'warning',
      }
    : {
        title: `从正式版撤下 ${label}？`,
        body: `从 TestFlight「iOS Stable」组移除，正式版用户不会再安装或更新到这个 build。已经安装的设备不受影响，要让他们换掉只能发一个更新的 build。${item.inBeta ? '测试版组里的保留不变。' : '这个 build 不在测试版组，撤下后不在任何 iOS 组里。'}`,
        confirmButtonText: '撤下',
        cancelButtonText: '取消',
        confirmButtonVariant: 'destructive',
      })
  if (confirmed !== 'confirm') {
    stableBusy.value = null
    return
  }
  try {
    const path = `admin/ios-builds/${item.build}/stable`
    const { release } = await (stable ? useAPI().post(path) : useAPI().delete(path)) as { release: Omit<IosReleaseNotes, 'changelog' | 'changelog_en' | 'revision'> | null }
    toast({ title: stable ? `${label} 已设为正式版` : `${label} 已从正式版撤下` })
    // The server returns the new membership; a withdrawn stable-only build is in no group (null).
    releaseNotes.value = release
      ? releaseNotes.value.map(row => (row.build === item.build ? { ...row, channel: release.channel, inBeta: release.inBeta, inStable: release.inStable } : row))
      : releaseNotes.value.filter(row => row.build !== item.build)
    void refreshTestFlightStatus(true)
  } catch (error) {
    toast({ title: stable ? '设为正式版失败' : '撤下失败', description: errorText(error), variant: 'destructive' })
    // Another admin may have changed the build: show the current state.
    void refreshReleaseNotes()
  } finally {
    stableBusy.value = null
  }
}

const notesPreview = (item: IosReleaseNotes) => (item.changelog || item.changelog_en).trim().split('\n')[0]?.replace(/^[-*·•]\s*/, '') ?? ''

async function refreshReleaseNotes() {
  releaseNotesLoading.value = true
  releaseNotesError.value = ''
  try {
    releaseNotes.value = await useAPI().get('admin/ios-release-notes') as IosReleaseNotes[]
  } catch (error) {
    releaseNotesError.value = errorText(error)
  } finally {
    releaseNotesLoading.value = false
  }
}

const pageLoading = computed(() => metricsLoading.value || testFlightLoading.value || releaseNotesLoading.value || storefrontsLoading.value)

const metricDisplayRows = computed<AppleMetricRow[]>(() => {
  if (!mergePlatforms.value) {
    return versionMetrics.value.map((row) => ({
      ...row,
      sourcePlatformCount: 1,
    }))
  }

  const rowsByVersion = new Map<string, AppleMetricRow>()
  for (const row of versionMetrics.value) {
    const existing = rowsByVersion.get(row.version)
    if (existing) {
      existing.installCount += row.installCount
      existing.activeUsers += row.activeUsers
      existing.heartbeatCount += row.heartbeatCount
      existing.launchCount += row.launchCount
      existing.usageEventCount += row.usageEventCount
      existing.trafficBytes += row.trafficBytes
      existing.trafficUpBytes += row.trafficUpBytes
      existing.trafficDownBytes += row.trafficDownBytes
      existing.eventDeviceCount += row.eventDeviceCount
      existing.eventUserCount += row.eventUserCount
      existing.lastHeartbeatAt = Math.max(existing.lastHeartbeatAt, row.lastHeartbeatAt)
      existing.lastEventAt = Math.max(existing.lastEventAt, row.lastEventAt)
      existing.lastSeenAt = Math.max(existing.lastSeenAt, row.lastSeenAt)
      existing.sourcePlatformCount += 1
      continue
    }

    rowsByVersion.set(row.version, {
      ...row,
      platform: 'all',
      sourcePlatformCount: 1,
    })
  }

  return [...rowsByVersion.values()]
})

async function refreshMetrics() {
  metricsLoading.value = true
  metricsError.value = ''
  try {
    versionMetrics.value = await useAxiom().getAppleVersionMetrics({
      durationDays: metricWindowDays.value,
      limit: 1000,
    })
  } catch (error) {
    metricsError.value = errorText(error)
    toast({
      title: '加载 Apple App 指标失败',
      description: metricsError.value,
      variant: 'destructive',
    })
  } finally {
    metricsLoading.value = false
  }
}

// PandaUnion caches the status for a minute; `refresh` reads App Store Connect again.
async function refreshTestFlightStatus(refresh = false) {
  testFlightLoading.value = true
  testFlightError.value = ''
  try {
    const data = await useAdmin.getAppleTestFlightStatus({ refresh })
    testFlightApps.value = data.apps
    testFlightFetchedAt.value = data.fetchedAt
  } catch (error) {
    testFlightError.value = errorText(error)
    toast({
      title: '加载 TestFlight 状态失败',
      description: testFlightError.value,
      variant: 'destructive',
    })
  } finally {
    testFlightLoading.value = false
  }
}

async function refreshAll(refresh = false) {
  await Promise.all([refreshMetrics(), refreshTestFlightStatus(refresh), refreshReleaseNotes(), refreshStorefronts()])
}

async function setMetricWindow(days: number) {
  if (metricWindowDays.value === days) return
  metricWindowDays.value = days
  await refreshMetrics()
}

function metricWindowLabel(days: number) {
  return days === 1 ? '24 小时' : `${days} 天`
}

function toggleMetricSort(key: MetricSortKey) {
  if (metricSort.value.key === key) {
    metricSort.value.direction = metricSort.value.direction === 'asc' ? 'desc' : 'asc'
    return
  }
  metricSort.value = {
    key,
    direction: key === 'version' || key === 'platform' ? 'asc' : 'desc',
  }
}

function sortMetricRows(rows: AppleMetricRow[]) {
  const { key, direction } = metricSort.value
  const directionMultiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const aValue = metricSortValue(a, key)
    const bValue = metricSortValue(b, key)
    let result: number
    if (typeof aValue === 'string' || typeof bValue === 'string') {
      result = String(aValue).localeCompare(String(bValue), 'en', { numeric: true, sensitivity: 'base' })
    } else {
      result = aValue - bValue
    }
    return result * directionMultiplier || b.installCount - a.installCount || b.trafficBytes - a.trafficBytes
  })
}

function metricSortValue(row: AppleMetricRow, key: MetricSortKey): string | number {
  if (key === 'users') return metricUserCount(row)
  if (key === 'platform') return metricPlatformLabel(row.platform)
  return row[key]
}

function metricUserCount(row: AppleVersionMetrics | AppleMetricRow) {
  return row.activeUsers || row.eventUserCount
}

function metricSortIcon(key: MetricSortKey) {
  if (metricSort.value.key !== key) return 'material-symbols:unfold-more'
  return metricSort.value.direction === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'
}

function errorText(error: any) {
  return error?.data?.message || error?.data?.error || error?.message || JSON.stringify(error, null, 2)
}

function platformLabel(platform: AppleVersionMetrics['platform']) {
  const labels: Record<AppleVersionMetrics['platform'], string> = {
    ios: 'iOS',
    ipados: 'iPadOS',
    tvos: 'tvOS',
    macos: 'macOS',
    visionos: 'visionOS',
    apple: 'Apple',
  }
  return labels[platform]
}

function metricPlatformLabel(platform: AppleMetricPlatform) {
  if (platform === 'all') return '全部 Apple'
  return platformLabel(platform)
}

function formatMetricNumber(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

function formatBytes(bytes?: number) {
  const value = Number(bytes || 0)
  if (!value) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = value
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n.toFixed(unit === 0 ? 0 : 2)} ${units[unit]}`
}

function formatMetricBytes(bytes?: number | null) {
  if (bytes === null || bytes === undefined) return '-'
  return formatBytes(Number(bytes || 0))
}

function formatMetricTime(epochMs?: number | null) {
  if (!epochMs) return '-'
  return dayjs(epochMs).format('MM-DD HH:mm')
}

function trafficMetricTitle(metrics?: AppleVersionMetrics | AppleMetricRow | null) {
  if (!metrics) return '-'
  return `${formatMetricBytes(metrics.trafficUpBytes)} up / ${formatMetricBytes(metrics.trafficDownBytes)} down`
}

function formatTestFlightTime(value?: string | null) {
  if (!value) return '-'
  return dayjs(value).format('MM-DD HH:mm')
}

type BadgeVariant = 'outline' | 'successOutline' | 'warning' | 'destructive'
// App Store Connect buildBetaDetails.externalBuildState; an unlisted state shows as sent.
const EXTERNAL_BUILD_STATES: Record<string, { label: string, variant: BadgeVariant }> = {
  PROCESSING: { label: '处理中', variant: 'warning' },
  PROCESSING_EXCEPTION: { label: '处理异常', variant: 'destructive' },
  MISSING_EXPORT_COMPLIANCE: { label: '缺出口合规', variant: 'warning' },
  IN_EXPORT_COMPLIANCE_REVIEW: { label: '出口合规审核中', variant: 'warning' },
  READY_FOR_BETA_SUBMISSION: { label: '未提交外部测试', variant: 'outline' },
  WAITING_FOR_BETA_REVIEW: { label: '等待外部测试审核', variant: 'warning' },
  IN_BETA_REVIEW: { label: '外部测试审核中', variant: 'warning' },
  BETA_REJECTED: { label: '外部测试被拒', variant: 'destructive' },
  BETA_APPROVED: { label: '外部测试已批准', variant: 'successOutline' },
  READY_FOR_BETA_TESTING: { label: '可外部测试', variant: 'successOutline' },
  IN_BETA_TESTING: { label: '外部测试中', variant: 'successOutline' },
  EXPIRED: { label: '已过期', variant: 'destructive' },
}
const BETA_REVIEW_STATES: Record<string, { label: string, className: string }> = {
  WAITING_FOR_REVIEW: { label: '等待审核', className: 'text-orange-600 dark:text-orange-400' },
  IN_REVIEW: { label: '审核中', className: 'text-orange-600 dark:text-orange-400' },
  APPROVED: { label: '审核通过', className: 'text-green-600 dark:text-green-400' },
  REJECTED: { label: '审核被拒', className: 'text-destructive' },
}
const externalBuildState = (state: string) => EXTERNAL_BUILD_STATES[state] ?? { label: state, variant: 'outline' as const }
const betaReviewState = (state: string) => BETA_REVIEW_STATES[state] ?? { label: state, className: 'text-muted-foreground' }
const testFlightPlatformLabel = (platform: string) => (platform === 'TV_OS' ? 'tvOS' : 'iOS')
const groupLatestBuildsLabel = (group: AppleTestFlightGroupStatus) =>
  group.latestBuilds.map(build => `${testFlightPlatformLabel(build.platform)} ${build.version}`).join(' · ')
// Removed testers still hold one of the external tester slots.
const testerSlotsNearlyFull = (app: AppleTestFlightAppStatus) => (app.testerSlotsUsed ?? 0) >= app.testerSlotLimit * 0.95


onMounted(() => refreshAll())
</script>

<template lang="pug">
div(class="mx-auto w-full max-w-7xl space-y-8 px-2 py-6")
  div(class="flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-start sm:justify-between")
    div(class="space-y-1")
      h1(class="text-2xl font-semibold tracking-tight") Apple 版本
      p(class="text-sm text-muted-foreground") PandaFan for Apple 的 iOS / iPadOS / tvOS / macOS 版本、心跳、启动和流量统计，以及 iOS 更新说明。不连接 TestFlight 发布。
    Button(variant="outline" size="sm" class="w-fit" :disabled="pageLoading" @click="refreshAll(true)")
      Icon(name="material-symbols:refresh" class="mr-2 h-4 w-4" :class="{ 'animate-spin': pageLoading }")
      | 刷新

  Card(class="gap-0 py-0" data-testid="testflight-status")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex items-center gap-2")
        CardTitle(class="text-base") TestFlight
        Badge(v-if="testFlightLoading" variant="secondary") loading
      p(class="mt-1 text-xs text-muted-foreground") 只读。各 group 人数和最新 build，iOS / tvOS 最近 3 个 build；{{ testFlightFetchedAt ? `数据时间 ${formatTestFlightTime(testFlightFetchedAt)}` : '尚未刷新' }}。
    CardContent(class="px-0 py-0")
      div(v-if="testFlightError" class="border-b px-6 py-3 text-sm text-destructive") {{ testFlightError }}
      div(v-if="testFlightLoading && !testFlightApps.length" class="py-8 text-center text-sm text-muted-foreground")
        div(class="inline-flex items-center gap-2")
          Icon(name="material-symbols:progress-activity" class="h-4 w-4 animate-spin")
          span 正在读取 TestFlight...
      div(class="grid divide-y divide-border/60 lg:grid-cols-2 lg:divide-x lg:divide-y-0")
        section(v-for="app in testFlightApps" :key="app.id" class="min-w-0 space-y-4 px-6 py-5" :data-testid="`testflight-app-${app.id}`")
          div(class="flex items-baseline justify-between gap-3")
            h3(class="text-sm font-semibold") {{ app.name }}
            div(class="flex items-center gap-1 text-sm tabular-nums")
              span(v-if="app.testerSlotsUsed !== null" :class="{ 'text-orange-600 dark:text-orange-400': testerSlotsNearlyFull(app) }") 名额占用约 {{ formatMetricNumber(app.testerSlotsUsed) }} / {{ formatMetricNumber(app.testerSlotLimit) }}
              Button(size="icon" variant="ghost" class="h-6 w-6 text-muted-foreground" aria-label="刷新 TestFlight" title="刷新 TestFlight" :aria-busy="testFlightLoading" :disabled="testFlightLoading" @click="refreshTestFlightStatus(true)")
                Icon(name="material-symbols:refresh" class="h-4 w-4" :class="{ 'animate-spin': testFlightLoading }")
          div(v-if="app.error" class="text-sm text-destructive") {{ app.error }}
          table(v-if="app.groups.length" class="w-full text-sm")
            tbody
              tr(v-for="group in app.groups" :key="group.id" class="border-b last:border-b-0")
                td(class="py-1.5 pr-3 font-medium whitespace-nowrap") {{ group.label }}
                td(class="py-1.5 pr-3 whitespace-nowrap tabular-nums")
                  span(v-if="group.latestBuilds.length") {{ groupLatestBuildsLabel(group) }}
                  span(v-else class="text-muted-foreground") 没有 build
                td(class="py-1.5 text-right whitespace-nowrap tabular-nums") {{ formatMetricNumber(group.testerCount) }} 人
          div(v-for="platform in app.platforms" :key="platform.platform" class="space-y-1.5")
            div(class="text-xs font-medium text-muted-foreground") {{ testFlightPlatformLabel(platform.platform) }}
            div(v-if="!platform.builds.length" class="text-sm text-muted-foreground") 没有 build
            div(v-for="build in platform.builds" :key="build.id" class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm")
              span(class="font-medium whitespace-nowrap tabular-nums") {{ build.marketingVersion }} ({{ build.version }})
              span(class="text-xs whitespace-nowrap text-muted-foreground tabular-nums") {{ formatTestFlightTime(build.uploadedDate) }}
              Badge(v-if="build.processingState !== 'VALID'" variant="warning") {{ build.processingState }}
              Badge(v-if="build.expired && build.externalBuildState !== 'EXPIRED'" variant="destructive") 已过期
              Badge(v-if="build.externalBuildState" :variant="externalBuildState(build.externalBuildState).variant" :title="build.externalBuildState") {{ externalBuildState(build.externalBuildState).label }}
              span(v-if="build.betaReviewState" class="text-xs whitespace-nowrap" :class="betaReviewState(build.betaReviewState).className" :title="build.betaReviewState") {{ betaReviewState(build.betaReviewState).label }}
              span(v-if="build.groups.length" class="text-xs text-muted-foreground") {{ build.groups.join(' · ') }}

  Card(class="gap-0 py-0" data-testid="external-purchase-storefronts")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex items-center gap-2")
        CardTitle(class="text-base") 网站购买链接地区
        Badge(v-if="storefrontsLoading" variant="secondary") loading
      p(class="mt-1 text-xs text-muted-foreground") 关闭定价的 App Store build 只在这些 storefront 显示网站购买链接；美国以外要先拿到 Apple 的 External Purchase entitlement。
    CardContent(class="space-y-3 px-6 py-4")
      div(v-if="storefrontsError" class="text-sm text-destructive") {{ storefrontsError }}
      div(class="flex flex-wrap items-center gap-2")
        span(v-for="code in storefronts" :key="code" class="inline-flex items-center gap-1 rounded-md border py-0.5 pr-1 pl-2 font-mono text-sm")
          | {{ code }}
          button(type="button" class="rounded-sm text-muted-foreground hover:text-foreground" :aria-label="`移除 ${code}`" :disabled="storefrontsSaving" @click="removeStorefront(code)")
            Icon(name="material-symbols:close" class="h-3.5 w-3.5")
        span(v-if="storefrontsLoaded && !storefronts.length" class="text-sm text-muted-foreground") 未开启，所有地区都不显示
      form(class="flex flex-wrap items-center gap-2" @submit.prevent="addStorefronts")
        Input(v-model="storefrontInput" placeholder="USA" aria-label="Storefront 代码" class="h-8 w-40 font-mono uppercase" :disabled="!storefrontsLoaded || storefrontsSaving")
        Button(type="submit" size="sm" variant="outline" class="h-8" :disabled="!storefrontsLoaded || storefrontsSaving || !storefrontInput.trim()") 添加
        Button(type="button" size="sm" class="h-8" :disabled="!storefrontsLoaded || !storefrontsDirty || storefrontsSaving" @click="saveStorefronts") 保存
        Button(v-if="storefrontsDirty" type="button" size="sm" variant="ghost" class="h-8" :disabled="storefrontsSaving" @click="acceptStorefronts(savedStorefronts)") 撤销

  Card(class="gap-0 py-0" data-testid="ios-release-notes")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          div(class="flex items-center gap-2")
            CardTitle(class="text-base") iOS 版本与更新说明
            Badge(v-if="releaseNotesLoading" variant="secondary") loading
          p(class="mt-1 text-xs text-muted-foreground") iOS Beta / iOS Stable 里最近 {{ RECENT_BUILDS }} 个 build。测试版 build 可一键加入正式版组（测试版组保留），有问题可从正式版组撤下。更新日志页只显示有说明的 build。
        div(class="flex flex-wrap items-center gap-2")
          Button(as-child size="sm" variant="ghost" class="h-8 w-fit")
            NuxtLink(to="/changelog?platform=ios" target="_blank")
              Icon(name="lucide:external-link" class="mr-1 h-4 w-4")
              | 更新日志页
          Button(size="sm" variant="outline" class="h-8 w-fit" :disabled="releaseNotesLoading" @click="refreshReleaseNotes")
            Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': releaseNotesLoading }")
            | 刷新
    CardContent(class="px-6 py-0")
      div(v-if="releaseNotesError" class="border-b py-3 text-sm text-destructive") {{ releaseNotesError }}
      div(class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-3 pr-4 font-medium") Build
              th(class="py-3 pr-4 font-medium") 线路
              th(class="py-3 pr-4 font-medium") 上传时间
              th(class="py-3 pr-4 font-medium") 更新说明
              th(class="py-3 pr-0")
          tbody
            tr(v-if="releaseNotesLoading && !releaseNotes.length")
              td(class="py-8 text-center text-muted-foreground" colspan="5")
                div(class="inline-flex items-center gap-2")
                  Icon(name="material-symbols:progress-activity" class="h-4 w-4 animate-spin")
                  span 正在读取 TestFlight build...
            tr(v-for="item in recentReleaseNotes" :key="item.build" class="border-b last:border-b-0" :data-testid="`ios-notes-row-${item.build}`")
              td(class="py-3 pr-4 font-medium whitespace-nowrap tabular-nums") {{ iosBuildLabel(item) }}
              td(class="py-3 pr-4")
                div(class="flex items-center gap-2 whitespace-nowrap")
                  Badge(:variant="item.channel === 'stable' ? 'default' : 'secondary'") {{ item.channel === 'stable' ? '正式版' : '测试版' }}
                  span(class="text-xs text-muted-foreground") {{ groupsLabel(item) }}
              td(class="py-3 pr-4 whitespace-nowrap text-muted-foreground") {{ formatTestFlightTime(item.uploadedAt) }}
              td(class="max-w-md py-3 pr-4")
                span(v-if="notesPreview(item)" class="line-clamp-1") {{ notesPreview(item) }}
                span(v-else class="text-muted-foreground") 未填写
              td(class="py-3 pr-0")
                div(class="flex justify-end gap-2")
                  Button(v-if="item.inStable" size="sm" variant="ghost" class="text-destructive hover:text-destructive" :disabled="stableBusy !== null" @click="setStable(item, false)") 撤下正式版
                  Button(v-else-if="item.inBeta" size="sm" variant="outline" :disabled="stableBusy !== null" @click="setStable(item, true)") 设为正式版
                  Button(size="sm" :variant="notesPreview(item) ? 'outline' : 'default'" @click="editingNotes = item") {{ notesPreview(item) ? '编辑' : '填写' }}
            tr(v-if="!releaseNotesLoading && !releaseNotes.length")
              td(class="py-8 text-center text-muted-foreground" colspan="5") TestFlight 里没有 iOS build

  Sheet(v-model:open="notesSheetOpen")
    SheetContent(side="right" class="w-full overflow-y-auto sm:max-w-2xl")
      SheetHeader
        SheetTitle iOS 更新说明
        SheetDescription {{ editingNotes ? `${iosBuildLabel(editingNotes)} · ${editingNotes.channel === 'stable' ? '正式版' : '测试版'}` : '' }}
      AdminDesktopReleaseNotesEditor(
        v-if="editingNotes"
        :key="editingNotes.build"
        :version="iosBuildLabel(editingNotes)"
        :notes-path="`admin/ios-release-notes/${editingNotes.build}`"
        :save-path="`admin/ios-release-notes/${editingNotes.build}`"
        hint="只用于这个 build；留空则不在更新日志页显示。"
        @saved="refreshReleaseNotes"
        class="mt-6"
      )

  Card(class="mt-8 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          div(class="flex items-center gap-2")
            CardTitle(class="text-base") Axiom 版本统计
            Badge(v-if="metricsLoading" variant="secondary") loading
          p(class="mt-1 text-xs text-muted-foreground") 最近 {{ metricWindowLabel(metricWindowDays) }}，{{ mergePlatforms ? '按版本合并 Apple 平台' : '按版本和平台' }} 统计心跳、启动、流量。
        div(class="flex flex-wrap items-center gap-3")
          label(class="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground")
            Checkbox#ios-merge-platforms(v-model="mergePlatforms")
            span 合并平台
          div(class="inline-flex overflow-hidden rounded-md border bg-background")
            Button(
              v-for="days in metricWindowOptions"
              :key="days"
              size="sm"
              :variant="metricWindowDays === days ? 'secondary' : 'ghost'"
              class="h-8 rounded-none border-0 px-3"
              :disabled="metricsLoading"
              @click="setMetricWindow(days)"
            ) {{ metricWindowLabel(days) }}
          Button(size="sm" variant="outline" class="h-8" :disabled="metricsLoading" @click="refreshMetrics")
            Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': metricsLoading }")
            | 指标
    CardContent(class="px-6 py-0")
      div(v-if="metricsError" class="border-b py-3 text-sm text-destructive") {{ metricsError }}
      div(class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('version')")
                  span 版本
                  Icon(:name="metricSortIcon('version')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'version' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('platform')")
                  span 平台
                  Icon(:name="metricSortIcon('platform')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'platform' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('installCount')")
                  span 安装设备
                  Icon(:name="metricSortIcon('installCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'installCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('users')")
                  span 用户
                  Icon(:name="metricSortIcon('users')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'users' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('heartbeatCount')")
                  span 心跳
                  Icon(:name="metricSortIcon('heartbeatCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'heartbeatCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('launchCount')")
                  span 服务启动
                  Icon(:name="metricSortIcon('launchCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'launchCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('usageEventCount')")
                  span 流量事件
                  Icon(:name="metricSortIcon('usageEventCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'usageEventCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('trafficBytes')")
                  span 流量总量
                  Icon(:name="metricSortIcon('trafficBytes')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'trafficBytes' }")
              th(class="py-3 pr-0 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('lastSeenAt')")
                  span 最新上报
                  Icon(:name="metricSortIcon('lastSeenAt')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'lastSeenAt' }")
          tbody
            tr(v-if="metricsLoading && !metricRows.length")
              td(class="py-10 text-center text-muted-foreground" colspan="9")
                div(class="inline-flex items-center gap-2")
                  Icon(name="material-symbols:progress-activity" class="h-4 w-4 animate-spin")
                  span 正在加载 Axiom 统计...
            tr(v-for="row in metricRows" :key="`${row.version}-${row.platform}`" class="border-b last:border-b-0")
              td(class="py-3 pr-4 font-medium") {{ row.version }}
              td(class="py-3 pr-4") {{ metricPlatformLabel(row.platform) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.installCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(metricUserCount(row)) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.heartbeatCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.launchCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.usageEventCount) }}
              td(class="py-3 pr-4 text-right tabular-nums" :title="trafficMetricTitle(row)") {{ formatMetricBytes(row.trafficBytes) }}
              td(class="py-3 pr-0 whitespace-nowrap") {{ formatMetricTime(row.lastSeenAt) }}
            tr(v-if="!metricsLoading && !metricRows.length")
              td(class="py-10 text-center text-muted-foreground" colspan="9") 暂无 Apple App Axiom 统计数据
</template>
